import { Injectable } from '@angular/core';

import { BUSINESS_CONST, BANK_INFO_CONST } from '../../constants/business.constants';
import { OpEnum } from '../../enums/op.enum';
import {
  DetailAccountModel,
  DetailRevolvingCreditModel,
  ProductCard,
} from '../../models/abre-cuentas.model';
import { AbreCuentasService } from '../abre-cuentas/abre-cuentas.service';
import { CaptureService } from '../capture/capture.service';
import { CopyService } from '../copies/copy.service';
import { CryptoVaultService } from '../external/crypto-vault/crypto-vault.service';
import { HelpersService } from '../helpers/helpers-onboarding.service';
import { FirmlyApprovedService } from '../firmly-approved/firmly-approved.service';
import { StringHelperService } from '../helpers/string-helper.service';
import { ValidationHelperService } from '../helpers/validation-helper.service';
import { ParametersService } from '../parameters/parameters.service';
import { ProductLoaderService } from '../product-loader/product-loader.service';
import { RequestService } from '../request/request-onboarding.service';
import { ChannelCommunicatorService } from '../communicator/channel-communicator/channel-communicator.service';
import { FormatNumberService } from '../../utils/format-number.service';

@Injectable()

/**
 * Inicialización de la clase PostValidateService, configuración del flujo para llamar cada vez que se necesite
 *
 * *Set PostValidateService class, flow config when is called when is needed*
 *
 * @access public
 * @type {class}
 */
export class PostValidateService {
  priority: any = 1;
  /**
   * Código de la respuesta de la solicitud
   *
   * *Response code by the request*
   *
   * @type {string} code
   */
  code: string;

  /**
   * Variable contador de casos
   *
   * *get case count variable*
   *
   * @type {number}
   */
  getCaseCount = 0;
  solicitationIdV: any;
  /**
   * Variable contador de casos
   *
   * *get case count variable*
   *
   * @type {number}
   */
  callTimeOut = 0;

  /**
   * Variable con la informacion de los tipos de productos
   *
   * *variable that contain the information of product type*
   *
   * @type {object}
   */
  ProductMulti: any;

  brandProduct: string;
  creditRechargeUpInformation: any = [];
  liabilities: any = [];
  /**
   * Constructor de la clase
   *
   * *Class Contstructor.*
   *
   * Inicializa los métodos para los modelos
   *
   * *Initialize method for models*
   *
   * @param {AbreCuentasService} abreCuentasService
   * @param {CommunicatorService} communicatorService
   * @param {ValidationHelperService} validationHelperService
   * @param {RequestService} requestService
   */
  constructor(
    public abreCuentasService: AbreCuentasService,
    private parametersService: ParametersService,
    private validationHelperService: ValidationHelperService,
    private stringHelperService: StringHelperService,
    private requestService: RequestService,
    private productLoaderService: ProductLoaderService,
    private captureService: CaptureService,
    private helperService: HelpersService,
    private cryptoVaultService: CryptoVaultService,
    private copyService: CopyService,
    private firmlyApprovedService: FirmlyApprovedService,
    private channel: ChannelCommunicatorService,
    private numberHelper: FormatNumberService,
    private cipherService: CryptoVaultService,
  ) { }

  /**
   * Función para cambiar los casos de la función
   *
   * *Switch function cases*
   *
   * @type {function}
   * @param {string} func
   * @returns {any}
   */
  switchFunction(func: string): any {
    switch (func) {
      case 'solicitationId':
        return this.solicitationId();
      case 'createClient':
        return this.createClient();
      case 'createContactInfoParticular':
        return this.createContactInfo('PARTICULAR');
      case 'createContactInfoLaboral':
        return this.createContactInfo('LABORAL');
      case 'prospectAddressesParticular':
        return this.prospectAddresses('PARTICULAR');
      case 'prospectAddressesLaboral':
        return this.prospectAddresses('LABORAL');
      case 'prospectAddressesCardSend':
        return this.prospectAddresses('CARD_SEND');
      case 'prospectAddresses':
        return this.prospectAddresses('DEFAULT');
      case 'createFinancials':
        return this.createFinancials();
      case 'foreignOperations':
        return this.foreignOperations();
      case 'createProducts':
        return this.createProducts();
      case 'performSolicitation':
        return this.performSolicitation();
      case 'getCase':
        return this.getCase();
      case 'getChannel':
        return this.channelCommunicator();
      case 'updateCaseAccountConfig':
        return this.updateCaseAccountConfig();
      case 'channelNotification':
        return this.getChannelNotification();
      default:
        break;
    }
  }

  /**
   * Función para obtener el código indentificador de la solicitud
   *
   * *Function that obtains solicitation id code*
   *
   * @type {function}
   * @returns {this.code}
   */
  async solicitationId() {
    const body = {
      data: {
        solicitation_type: '0003',
      },
    };
    await this.requestService.createCaseSolicitationId(body).then(
      response => {
        console.log('SolicitationID: ', response);
        try {
          this.abreCuentasService.setSolicitationId(response);
          this.code = '200';
        } catch (error) {
          this.code = '500';
        }
      },
      () => {
        this.code = '500';
      },
    );
    return this.code;
  }

  /**
   * Función para obtener notificación a canal
   *
   * *Function that obtains channel notification*
   *
   * @type {function}
   * @returns {this.code}
   */
  async channelCommunicator() {
    setTimeout(async () => {
      await this.captureService
        .getChannelComunicator()
        .then((res: any) => {
          this.code = res;
        })
        .catch(() => {
          this.code = '500';
        });
    }, 30000);
    return this.code;
  }

  /**
   * Función para envíar al servicio la información de contácto del usuario, retorna el código de éxito de la solicitud
   *
   * *Function that sends to the service user's contact information, returns solicitation success code*
   *
   * @type {function}
   * @param {string} type
   * @returns {this.code}
   */
  async createContactInfo(type: string) {
    await this.captureService.createContactInfo(type).then(() => {
      try {
        this.code = '200';
      } catch (error) {
        this.code = '500';
      }
    }, () => {
      return this.code = '500';
    });
    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función para envíar al servicio la información básica del usuario (creación del cliente), retorna el código de éxito de la solicitud
   *
   * *Function that sends to the service user's contact information (client creation), returns solicitation success code*
   *
   * @type {function}
   * @returns {this.code}
   */
  async createClient() {
    await this.captureService.createClient().then(() => {
      try {
        this.code = '200';
      } catch (error) {
        console.log(error);
        throw 500;
      }
    }, () => {
      return this.code = '500';
    });

    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función para envíar al servicio la información de dirección del usuario, retorna el código de éxito de la solicitud
   *
   * *Function that sends to the service user contact information depending on the case, returns solicitation success code*
   *
   * @type {function}
   * @returns {this.code}
   */
  async prospectAddresses(type: string) {
    await this.captureService.prospectAddresses(type).then(() => {
      try {
        this.code = '200';
      } catch (error) {
        this.code = '500';
      }
    }, () => {
      return this.code = '500';
    });
    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función para envíar al servicio la información financiera del usuario, retorna el código de éxito de la solicitud
   *
   * *Function that sends to the service user's financial information, returns solicitation success code*
   *
   * @type {function}
   * @returns {this.code}
   */
  async createFinancials() {
    await this.cryptoVaultService.refreshKeyCrypto();
    setTimeout(async () => {
      await this.captureService.createFinancials().then(() => {
        try {
          this.code = '200';
        } catch (error) {
          this.code = '500';
        }
      }, () => {
        return this.code = '500';
      });
    }, 1000);

    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función para envíar al servicio la información de operaciones extrangeras del usuario, retorna el código de éxito de la solicitud
   *
   * *Function that sends to the service user information of foreign operation, return solicitation success code*
   *
   * @type {function}
   * @returns {this.code}
   */
  async foreignOperations() {
    const {
      operationCurrencyList,
      foreignProductsInfo,
      foreignProductsSelected,
      foreignCurrencyModel,
    } = this.abreCuentasService.getAbreCuentasModel();

    let body: any = {
      general_foreign_ops: false,
      foreign_currency_investments: false,
      foreign_currency_payments: false,
      foreign_currency_other_ops: false,
      imports_operations: false,
      exports_operations: false,
      titular_products_foreign_cur: false,
      foreign_currency_alternative_ops: false,
      foreign_product_is_credit: false,
      foreign_product_amount: {
        currency: '',
        amount: 0,
      },
    };

    if (this.abreCuentasService.getPostlogin()) {
      try {
        const { foreign } = this.abreCuentasService.getCustomerData();
        const {
          exports_operations,
          foreign_currency_investments,
          foreign_currency_other_ops,
          foreign_product_amount,
          foreign_currency_payments,
          general_foreign_ops,
          imports_operations,
          titular_products_foreign_cur,
          foreign_product_city,
          foreign_product_company,
          foreign_product_country,
          foreign_product_is_credit,
          foreign_product_number,
          foreign_currency_alternative_ops,
          foreign_product_type,
        } = foreign;
        if (foreign) {
          if (general_foreign_ops === 'Si' || general_foreign_ops === 'SI') {
            body = {
              ...body,
              general_foreign_ops:
                this.helperService.mapBooleanData(general_foreign_ops),
              foreign_currency_alternative_ops:
                this.helperService.mapBooleanData(
                  foreign_currency_alternative_ops,
                ),
              foreign_currency_investments: this.helperService.mapBooleanData(
                foreign_currency_investments,
              ),
              foreign_currency_payments: this.helperService.mapBooleanData(
                foreign_currency_payments,
              ),
              foreign_currency_other_ops: this.helperService.mapBooleanData(
                foreign_currency_other_ops,
              ),
              imports_operations:
                this.helperService.mapBooleanData(imports_operations),
              exports_operations:
                this.helperService.mapBooleanData(exports_operations),
              foreign_product_is_credit: this.helperService.mapBooleanData(
                foreign_product_is_credit,
              ),
            };
          }

          if (titular_products_foreign_cur === 'Si' || titular_products_foreign_cur === 'SI') {
            let country = this.abreCuentasService.filterCountryListxName(foreign_product_country);
            body = {
              ...body,
              titular_products_foreign_cur: this.helperService.mapBooleanData(
                titular_products_foreign_cur,
              ),
              general_foreign_ops: this.helperService.mapBooleanData(
                titular_products_foreign_cur,
              ),
              foreign_product_type: foreign_currency_alternative_ops,
              foreign_product_number,
              foreign_product_city,
              foreign_product_country: country.country_code,
              foreign_product_amount,
              foreign_product_company,
            };
          }
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      if (foreignCurrencyModel && foreignCurrencyModel.elementId && foreignCurrencyModel.elementId !== 2) {
        const foreign_ops = {
          imports: operationCurrencyList.find(data => data.id === 771),
          exports: operationCurrencyList.find(data => data.id === 772),
          investments: operationCurrencyList.find(data => data.id === 774),
          credit: operationCurrencyList.find(data => data.id === 776),
          other: operationCurrencyList.find(data => data.id === 781),
          payments: operationCurrencyList.find(data => data.id === 780),
        };
        body = {
          ...body,
          general_foreign_ops: foreignCurrencyModel.checked
            ? foreignCurrencyModel.checked
            : false,
          foreign_currency_alternative_ops: foreignCurrencyModel
            ? foreignCurrencyModel.checked
            : false,
          foreign_currency_investments: foreign_ops.investments
            ? foreign_ops.investments.checked
            : false,
          foreign_currency_payments: foreign_ops.payments
            ? foreign_ops.payments.checked
            : false,
          foreign_currency_other_ops: foreign_ops.other
            ? foreign_ops.other.checked
            : false,
          imports_operations: foreign_ops.imports
            ? foreign_ops.imports.checked
            : false,
          exports_operations: foreign_ops.exports
            ? foreign_ops.exports.checked
            : false,
          foreign_product_is_credit: foreign_ops.credit
            ? foreign_ops.credit.checked
            : false,
        };
      }

      if (foreignProductsSelected && foreignProductsSelected.elementId && foreignProductsSelected.elementId !== 2) {
        body = {
          ...body,
          titular_products_foreign_cur: foreignProductsSelected.checked,
          general_foreign_ops:
            foreignCurrencyModel.elementId !== 2 && foreignCurrencyModel.checked
              ? foreignCurrencyModel.checked
              : false,
          foreign_product_type: foreignProductsInfo.product_type,
          foreign_product_number: foreignProductsInfo.product_id,
          foreign_product_city: '',
          foreign_product_country: foreignProductsInfo.country.country_code,
          foreign_product_amount: {
            currency: foreignProductsInfo.currency
              .normalize('NFD')
              .replace(/[\u0300-\u0301]/g, ''),
            amount: +foreignProductsInfo.amount,
            decimals: 0,
          },
          foreign_product_company: foreignProductsInfo.entity_name,
        };
      }
    }

    if (!this.validationHelperService.isPassiveProduct()) {
      body = [
        {
          document: {
            type: this.abreCuentasService.getDocumentInfo().document_type,
            number: this.abreCuentasService.getDocumentInfo().document_number,
          },
          ...body,
        },
      ];
    }

    const solicitationId = this.abreCuentasService.getSolicitationId();
    await this.requestService
      .foreignOperationsCase({ data: body }, solicitationId)
      .then(() => {
        try {
          this.code = '200';
        } catch (error) {
          this.code = '500';
        }
      }, () => {
        return this.code = '500';
      });

    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función para crear el producto seleccionado por el usuario, retorna el código de éxito de la solicitud
   *
   * *Function that creates the product selected by the user, return solicitation success code*
   *
   * @type {function}
   * @returns {this.code}
   */
  async createProducts() {
    console.log(
      'Create Product >>>>> AbreCuentasService >>> ',
      this.abreCuentasService,
    );
    const self = this.abreCuentasService.getAbreCuentasModel();
    const productCode = this.abreCuentasService.getproductSelected();
    const prospect = this.abreCuentasService.getProspectResponse();
    const postLogin = this.abreCuentasService.getPostlogin();
    const productType =
      this.abreCuentasService.getproductSelected().product_type;
    this.ProductMulti = this.parametersService.getSetting(OpEnum.LIST_MULTI);
    let rate = [{}];

    const productsmulti = this.abreCuentasService.getProductsSelects();
    let products;
    if (productType === BUSINESS_CONST.PRODUCT_TYPE_FREE ||
      productsmulti.find(find => find.product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE)) {
      products = {
        product_type: productCode.product_code,
        product_subtype: '533',
      };
      console.log('Linea 432 >>>> CreditParms >>> ', self.creditParms);
      const taxRateValue =
        !this.firmlyApprovedService.isEmpty() &&
          this.firmlyApprovedService.getOfferType_Channel() === 'En_firme'
          ? self.creditParms.rateValue['rate_NA']
          : this.abreCuentasService.getRatesOffer();
      console.log('taxRateValue', taxRateValue);
      rate = [taxRateValue];
    } else if (productType === BUSINESS_CONST.PRODUCT_TYPE_LIVING) {
      products = {
        product_type:
          this.abreCuentasService.getOtherSimulationData().sub_product,
        product_subtype: this.abreCuentasService.getOtherSimulationData().code,
      };
    } else if (productType === BUSINESS_CONST.PRODUCT_TYPE_TC) {
      products = {
        product_type: productCode.product_code,
      };
    } else {
      products = {
        product_type: 'CRP', // productCode.product_code,
        product_subtype:
          prospect.client === false
            ? '131'
            : this.helperService.getProductCodeBySegmentId(
              self.basicModel.sub_segment_id,
            ),
      };
    }
    let body = {};
    if (productType === BUSINESS_CONST.PRODUCT_TYPE_TC) {
      switch (self.paydayProductModel.selectedProduct.elementId) {
        case 1:
          this.brandProduct = BUSINESS_CONST.MASTER;
          break;
        case 2:
          this.brandProduct = BUSINESS_CONST.VISA;
          break;
        case 3:
          this.brandProduct = BUSINESS_CONST.VISALM;
          break;
      }
      body = {
        brand:
          self.paydayProductModel.selectedProduct.elementId === 1
            ? BUSINESS_CONST.MASTER
            : BUSINESS_CONST.VISA,
        brand_code: self.cardType.bin,
        brand_details: this.abreCuentasService.isGrayZone()
          ? this.brandProduct
          : self.cardType.brand_details,
        affinity: '1',
        affinity_code: self.cardType.afinity_code,
        invoice_cycle:
          self.paydayProductModel.selectedPayDay.elementId === 10 ? '3' : '1', // 3 - 30 y 1 - 15
        segment_code: self.cardType.part_segment_code,
      };
    } else if (productType == BUSINESS_CONST.PRODUCT_TYPE_LIVING) {
      body = {
        ...body,
        city: this.abreCuentasService.getSimulatorModel().city,
      };
    }
    console.log('body', body);
    let ProductsList;
    if (
      !this.firmlyApprovedService.isEmpty() &&
      this.firmlyApprovedService.getOfferType_Channel() === 'En_firme'
    ) {
      ProductsList = this.firmlyApprovedService.getDetailProductsMulti();
    } else {
      switch (productType) {
        case BUSINESS_CONST.MULTI:
          ProductsList = self.pcoModel.granted_product_multi;
          break;
        default:
          ProductsList =
            self.pcoModel.granted_product_multi.length > 0
              ? self.pcoModel.granted_product_multi
              : [self.pcoModel.granted_product];
          break;
      }
    }
    body = this.body(
      body,
      self,
      ProductsList,
      productType,
      rate,
      postLogin,
      prospect,
    );
    console.log('--------PRODUCTS------------');
    console.warn('Body del producto a enviar ---->>> \n', body);
    const solicitationId = this.abreCuentasService.getSolicitationId();
    await this.requestService
      .createProduct({ data: body }, solicitationId)
      .then(() => {
        try {
          this.code = '200';
        } catch (error) {
          console.log('Error creando producto >>>> ', error);
          this.code = '500';
        }
      }, () => {
        return this.code = '500';
      })
      .catch(error => {
        console.log('Error creando producto >>>> ', error);
        this.code = '500';
      });

    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función de desempeño de la solicitud
   *
   * *Function perform solicitation*
   *
   * @type {function}
   * @returns {this.code}
   */
  async performSolicitation() {
    const body = {
      data: [
        {
          solicitation_type: 'tskAvanzarProcesoDigital',
        },
      ],
    };

    const solicitationId = this.abreCuentasService.getSolicitationId();
    await this.requestService
      .performSolicitation(body, solicitationId)
      .then(() => {
        try {
          this.code = '200';
        } catch (error) {
          this.code = '500';
        }
      });
    // this.productLoaderService.hide();
    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función para obtener el caso
   *
   * *Function that gets the case*
   *
   * @type {function}
   * @returns {this.code}
   */
  async getCase() {
    this.productLoaderService.show(
      this.copyService.copies.show_create_product,
      this.parametersService.getSetting(OpEnum.GET_CASE_TIME_OUTS)[
      'first-timeout'
      ] * 1000,
    );
    if (this.callTimeOut === 0) {
      await this.abreCuentasService.sleep(
        this.parametersService.getSetting(OpEnum.GET_CASE_TIME_OUTS)[
        'first-timeout'
        ] * 1000,
      );
    }
    setTimeout(() => {
      this.cryptoVaultService.refreshKeyCrypto();
    }, (this.parametersService.getSetting(OpEnum.GET_CASE_TIME_OUTS)['first-timeout'] * 1000) / 2);
    const solicitationId = this.abreCuentasService.getSolicitationId();
    const productCode =
      this.abreCuentasService.getproductSelected().product_code;
    await this.requestService
      .getCaseSolicitationInfo(solicitationId, productCode)
      .then(async response => {
        this.getCaseCount += 1;
        try {
          if (response.status === 200) {
            const resp = response.body.data;
            const card: ProductCard = resp;
            const documentNumber =
              this.abreCuentasService.getDocumentInfo().document_number;
            this.abreCuentasService.setProductCard(card);
            switch (card.solicitation_status.status_code) {
              case '03':
                if (this.abreCuentasService.isGrayZone()) {
                  this.code = '201';
                  break;
                }
                await this.abreCuentasService.sleep(
                  this.parametersService.getSetting(OpEnum.GET_CASE_TIME_OUTS)[
                  'second-timeout'
                  ] * 1000,
                );
                if (this.getCaseCount === 2) {
                  this.code = 'secondCall';
                } else {
                  this.callTimeOut++;
                  return this.getCase();
                }
                break;
              case '01':
              case '02':
                this.code = '127';
                break;
              case '00':
                if (
                  this.abreCuentasService.getproductSelected().name ===
                  BUSINESS_CONST.PRODUCT_TYPE_REVOLVING ||
                  this.abreCuentasService.getproductSelected().name ===
                  BUSINESS_CONST.PRODUCT_TYPE_FREE
                ) {
                  const productDetail = new DetailAccountModel();
                  const detailRevolvingCredit =
                    new DetailRevolvingCreditModel();
                  productDetail.account_number =
                    resp.destination_account.account_id;
                  productDetail.create_date_account = resp.transaction_date;
                  detailRevolvingCredit.available_amount =
                    resp.available_amount;
                  detailRevolvingCredit.product_id = resp.product_id;
                  this.abreCuentasService.setDetailAccountCreation(
                    productDetail,
                  );
                  this.abreCuentasService.setDetailRevolvingCreditCreation(
                    detailRevolvingCredit,
                  );
                }
                this.code = '200';
                break;
              case '04':
                this.code = '200';
                break;
              case '07':
                this.code = '129';
                break;
              case '05':
                this.code = '130';
                break;
              case '06':
                this.code = '201';
                break;
              default:
                this.code = '127';
                break;
            }
          } else {
            this.code = '127';
          }
        } catch (error) {
          this.code = '127';
        }
      });
    this.productLoaderService.hide();
    return this.code;
  }

  /* istanbul ignore next */

  /**
   * Función para actualizar la configuración del caso de la cuenta
   *
   * *Function that updates case account config*
   *
   * @type {function}
   * @returns {this.code}
   */
  async updateCaseAccountConfig() {
    let body: any = {
      solicitation_id: this.abreCuentasService.getSolicitationId(),
      card_delivery_type: this.abreCuentasService.getReceiveCard(),
      is_benefit_exempt: null,
    };
    if (
      this.abreCuentasService.getproductSelected().product_type ===
      BUSINESS_CONST.PRODUCT_TYPE_PAYROLL
    ) {
      body = {
        ...body,
        is_benefit_exempt:
          this.abreCuentasService.getAgreementSelected().transaction_type,
      };
    }
    if (
      this.abreCuentasService.viewOptionMultiProduct &&
      this.abreCuentasService.getStatusCreate()
    ) {
      body = {
        ...body,
        account_type: 'AHO',
        account_subtype: '533',
        campaign_id: '',
      };
    }
    await this.requestService
      .updateCaseAccountConfig({ data: body })
      .then(() => {
        try {
          this.code = '200';
        } catch (error) {
          this.code = '500';
        }
      });
    return this.code;
  }

  body(tdc, self, ProductsList, productType, rate, postLogin, prospect) {
    console.warn(
      '----------------- Body Post-Validate ---------------------------',
    );
    console.log('Self: ', self);
    let allies = this.channel.getChannelId() === '163';
    let itemsSelected;
    if (allies) {
      itemsSelected = [this.abreCuentasService.getproductSelected()] as any;
    } else {
      itemsSelected = this.abreCuentasService.getProductsSelects();
    }
    let indexTc = 1;
    const virtualCardFranchise =
      self.virtualCardFranchiseModel !== undefined &&
        self.virtualCardFranchiseModel.selectedFranchise !== undefined
        ? self.virtualCardFranchiseModel.selectedFranchise
        : { label: '' };
    let body: any = [];
    const brandMap = {
      1: BUSINESS_CONST.MASTER,
      2: BUSINESS_CONST.VISA,
      3: BUSINESS_CONST.VISALM,
    };
    if (ProductsList[0].product_type !== BUSINESS_CONST.PRODUCT_TYPE_LIVING) {
      if (!allies) {
        itemsSelected = itemsSelected.sort(
          (product1, product2) =>
            Number(product1.prioritizeForClient) -
            Number(product2.prioritizeForClient),
        );
      }
      let products: any = [];
      let filterProductsTC: any = [];
      for (const item of itemsSelected) {
        let filterProductsByType = ProductsList.filter(
          prod => prod.product_type === item.product_type,
        );
        products.push(filterProductsByType[0]);
        if (
          item.product_type === BUSINESS_CONST.PRODUCT_TYPE_TC &&
          filterProductsByType.length > 1
        ) {
          filterProductsTC = filterProductsByType.slice(
            1,
            filterProductsByType.length,
          );
        }
      }
      if (filterProductsTC.length > 0) {
        products = products.concat(filterProductsTC);
      }
      ProductsList = products;
    }

    ProductsList.forEach((product, index) => {
      console.log('index->', index);
      console.log('Product: ', product);
      const cardType = product.brand && self.cardTypes[product.brand]
        ? self.cardTypes[product.brand]
        : self.cardType;
      console.warn('CardTypes: ', cardType, '\nProduct: ', product);
      console.warn('VirtualCardFranchise: ', virtualCardFranchise);
      const virtualCardFlag =
        (product.brand === undefined &&
          product.product_type === BUSINESS_CONST.PRODUCT_TYPE_TC) ||
        (product.brand && product.brand === virtualCardFranchise.label);

      let bodyProduct = {
        ...tdc,
        refund_information: this.refundInfoByProduct(product),
        offer_id:
          this.firmlyApprovedService.getOfferId_Channel() !== ''
            ? this.firmlyApprovedService.getOfferId_Channel()
            : '',

        campaign_id:
          cardType.campaign_code !== undefined ? cardType.campaign_code : 0,
        client_name: `${self.documentModel.firstname || ''} ${self.documentModel.middlename
          ? self.documentModel.middlename + ' '
          : ''
          }${self.documentModel.primary_lastname} ${self.documentModel.second_lastname
          }`,
        available_amount: [this.validAmountFirmlyApproved(product)],
        brand:
          product.product_type !== BUSINESS_CONST.PRODUCT_TYPE_TC
            ? ''
            : self.paydayProductModel.selectedProduct.elementId === 1
              ? BUSINESS_CONST.MASTER
              : BUSINESS_CONST.VISA,
        brand_code:
          product.product_type !== BUSINESS_CONST.PRODUCT_TYPE_TC
            ? ''
            : cardType.bin,
        brand_details:
          product.product_type !== BUSINESS_CONST.PRODUCT_TYPE_TC
            ? ''
            : this.abreCuentasService.isGrayZone()
              ? brandMap[self.paydayProductModel.selectedProduct.elementId]
              : cardType.brand_details,
        affinity: '1',
        affinity_code:
          product.product_type !== BUSINESS_CONST.PRODUCT_TYPE_TC
            ? ''
            : cardType.afinity_code,
        invoice_cycle:
          product.product_type !== BUSINESS_CONST.PRODUCT_TYPE_TC
            ? ''
            : self.paydayProductModel.selectedPayDay.elementId === 10
              ? '3'
              : '1',
        segment_code:
          product.product_type !== BUSINESS_CONST.PRODUCT_TYPE_TC
            ? ''
            : cardType.part_segment_code,
        card_virtual_flag: virtualCardFlag,
        billing_date: '2022-04-01T00:00:00',
        rate: this.validRateFirmlyApproved(product),
        term: {
          period:
            this.abreCuentasService.getAbreCuentasModel().creditParms
              .maxPeriodValue,
          frecuency: 'MM',
        },
        blocked: false,
        is_beneficiary: false,
        product: {
          product_id:
            product.product_type === BUSINESS_CONST.PRODUCT_TYPE_TC
              ? '' + indexTc++
              : '',
          product_type:
            product.product_type === BUSINESS_CONST.PRODUCT_TYPE_LIVING
              ? this.abreCuentasService.getOtherSimulationData().sub_product
              : this.productCodeSelected(product.product_type),
          product_subtype:
            product.product_type === BUSINESS_CONST.PRODUCT_TYPE_LIVING
              ? this.abreCuentasService.getOtherSimulationData().code
              : this.productCodeSelected(product.product_type) === 'ORD'
                ? '533'
                : this.productCodeSelected(product.product_type) === 'TCR'
                  ? ''
                  : this.productCodeSelected(product.product_type) === BUSINESS_CONST.PRODUCT_TYPE_LIB
                    ? '371'
                    : prospect.client === false
                      ? '131'
                      : this.helperService.getProductCodeBySegmentId(
                        self.basicModel.sub_segment_id,
                      ),
        },
        priority: index + 1,
        installments_quantity:
          this.productCodeSelected(product.product_type) === BUSINESS_CONST.PRODUCT_CODE_ORD
            || this.productCodeSelected(product.product_type) === BUSINESS_CONST.PRODUCT_TYPE_LIB
            ? this.abreCuentasService.getAbreCuentasModel().creditParms
              .maxPeriodValue
            : '0',
      };
      if (product.product_type === BUSINESS_CONST.PRODUCT_TYPE_LOAN) {
        bodyProduct = this.fieldLoan(bodyProduct);
      }
      console.log('bodyProduct ajustado: ', bodyProduct);
      if (
        product.product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE ||
        product.product_type === BUSINESS_CONST.PRODUCT_TYPE_REVOLVING ||
        product.product_type === BUSINESS_CONST.PRODUCT_TYPE_LOAN
      ) {
        const createProduct =
          product.product_type === BUSINESS_CONST.PRODUCT_TYPE_REVOLVING ? this.abreCuentasService.getAssociateRevolving() :
            this.abreCuentasService.getcreateProductSelected();
        if (postLogin && createProduct !== undefined) {
          bodyProduct.destination_account = {
            account_id: createProduct.account_id,
            account_type: createProduct.account_type,
            account_subtype: createProduct.account_subtype,
          };
          bodyProduct.refund_information = this.refundInfo(product, createProduct);
        } else if (!this.firmlyApprovedService.isEmpty() && this.firmlyApprovedService.getOfferType_Channel() === 'En_firme') {
          console.log('Refund: ', bodyProduct);
          bodyProduct.refund_information[0].refund_method = 'AC';
          bodyProduct.refund_information[0].branch_id = this.abreCuentasService.getAgencySelected()
            ? this.abreCuentasService.getAgencySelected().agency_code
            : '';
          bodyProduct.refund_information[0].amount = this.validAmountFirmlyApproved(product);
          console.log('Refund modificado: ', bodyProduct);
        }
        if (product.product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE ||
          product.product_type === BUSINESS_CONST.PRODUCT_TYPE_LOAN) {
          (bodyProduct.installments_quantity = Number(
            self.creditParms.maxPeriodValue,
          )),
            (bodyProduct['billing_date'] =
              this.abreCuentasService.getPAyDay(true)),
            (bodyProduct.rate = this.validRateFirmlyApproved(product, rate));
          bodyProduct.origin_amount = [
            {
              currency: BANK_INFO_CONST.CURRENCY,
              amount: self.creditParms.maxAmountValue.toString(),
              decimals: 0,
            },
          ];
          bodyProduct.term = {
            period:
              this.abreCuentasService.getAbreCuentasModel().creditParms
                .maxPeriodValue,
            frecuency: 'MM',
          };
          if (
            !this.firmlyApprovedService.isEmpty() &&
            this.firmlyApprovedService.getOfferType_Channel() === 'En_firme'
          ) {
            bodyProduct.rates = this.formatRateFirmlyApproved(this.abreCuentasService.getAbreCuentasModel().creditParms.rateValue);
          }
        }
      }
      // purchase_option solo en leaseback LEA 07, compra cartera LEA o HIP 03 55, vivienda LEA o HIP 52 01
      if (productType === BUSINESS_CONST.PRODUCT_TYPE_LIVING) {
        bodyProduct.origin_amount = [
          {
            currency: BANK_INFO_CONST.CURRENCY,
            amount: self.creditParms.maxAmountValue.toString(),
            decimals: 0,
          },
        ];
        bodyProduct.installments_quantity = Number(
          self.creditParms.maxPeriodValue,
        );
        bodyProduct.assisted_housing_flag =
          self.simulatorModel.assisted_housing_flag;
        bodyProduct.new_housing_flag = self.simulatorModel.new_housing_flag;
        bodyProduct.financing_type = '1';
        if (
          self.otherSimulationData.code === '01' ||
          self.otherSimulationData.code === '52' ||
          self.otherSimulationData.code === '55' ||
          self.otherSimulationData.code === '03' ||
          self.otherSimulationData.code === '07'
        ) {
          bodyProduct.purchase_option = self.simulatorModel.purchase_option;
        }
        bodyProduct.own_project_flag = self.simulatorModel.own_project_flag;
        bodyProduct.capital_amount = [
          {
            currency: self.simulatorModel.property_amount.currency,
            amount: self.simulatorModel.property_amount.amount,
            decimals: self.simulatorModel.property_amount.decimal,
          },
        ];

        bodyProduct.term = {
          period:
            this.abreCuentasService.getAbreCuentasModel().creditParms
              .maxPeriodValue,
          frecuency: 'MM',
        };
        if (
          self.otherSimulationData.code === '03' ||
          self.otherSimulationData.code === '55'
        ) {
          bodyProduct.liabilities = [
            {
              liability_id: self.simulatorModel.libialities.liability_id,
              bank_name: self.simulatorModel.libialities.liability_id,
              bank_id: self.simulatorModel.libialities.bank_name,
              payoff_amount: {
                currency: self.simulatorModel.available_amount.currency,
                amount: self.simulatorModel.available_amount.amount,
                decimals: self.simulatorModel.available_amount.decimals,
              },
              monthly_fee_amount: self.simulatorModel.fee,
            },
          ];
        }
        bodyProduct.leasing_contract = self.simulatorModel.leasing_contract;
      }
      console.warn('Body ajustado ---->\n', bodyProduct);
      body.push(bodyProduct);
      if (bodyProduct.rates) {
        this.requestService.setRates(bodyProduct.rates);
      }
    });
    console.warn(
      '----------------- Fin Body Post-Validate ---------------------------',
    );
    return body;
  }

  refundInfo(product, createProduct) {
    if (this.abreCuentasService.haveLoan && product.product_type !== BUSINESS_CONST.PRODUCT_TYPE_REVOLVING) {
      return this.loanRefund(product);
    } else {
      return [{
        refund_method: 'AC',
        account: {
          account_id: createProduct.account_id,
          account_type: createProduct.account_type,
          account_subtype: createProduct.account_subtype,
        },
        branch_id: this.abreCuentasService.getAgencySelected()
          ? this.abreCuentasService.getAgencySelected().agency_code
          : '',
        amount: this.validAmountFirmlyApproved(product),
      }];
    }
  }

  fieldLoan(body) {
    const loanSelected = this.abreCuentasService.getRequestLoanSelected().elementId;
    if (this.abreCuentasService.haveLoan) {
      const destinity = {
        destinity: loanSelected,
      };
      body = Object.assign(body, destinity);
      const fee_amount = {
        fee_amount: {
          currency: BANK_INFO_CONST.CURRENCY,
          amount: this.abreCuentasService.getSaveValueInputAmount() ? this.abreCuentasService.getSaveValueInputAmount() : '0',
          decimals: 0,
        },
      };
      body = Object.assign(body, fee_amount);
      if (loanSelected !== '01') {
        this.infoCredit();
        const passive = {
          liabilities: this.liabilities,
          credit_recharge_up_information: this.creditRechargeUpInformation,
        };
        body = Object.assign(body, passive);
      }
      const destination = {
        product_destination_type: this.abreCuentasService.getProductDestinationType(),
      };
      if (body.product) {
        body.product = Object.assign(body.product, destination);
      }
    }
    return body;
  }

  infoCredit() {
    switch (this.abreCuentasService.getRequestEntity()) {
      case 1:
        this.creditItau();
        break;
      case 2:
        this.creditOther();
        break;
      default:
        this.creditItau();
        this.creditOther();
        break;
    }
  }

  creditItau() {
    const loans = this.abreCuentasService.getCreditList();
    loans.map((i: any) => {
      this.creditRechargeUpInformation.push({
        account_subtype: i.type,
        credit_recharge_up_id: i.number,
        payoff_amount: {
          currency: BANK_INFO_CONST.CURRENCY,
          amount: i.amount,
          decimals: 0,
        },
        // toDo
        monthly_fee_amount: {
          currency: BANK_INFO_CONST.CURRENCY,
          amount: '',
          decimals: 0,
        },
      });
    });
  }

  creditOther() {
    const loansInfo = this.abreCuentasService.getFormLoan();
    loansInfo.addLoanForm.map((i: any) => {
      this.liabilities.push({
        bank_id: i.nitEntityForm,
        liability_id: i.numProductForm,
        bank_name: i.nameEntityForm,
        payoff_amount: {
          currency: BANK_INFO_CONST.CURRENCY,
          amount: i.valuePortfolioForm,
          decimals: 0,
        },
        monthly_fee_amount: {
          currency: BANK_INFO_CONST.CURRENCY,
          amount: i.freeValueForm,
          decimals: 0,
        },
      });
    });
  }

  productCodeSelected(productType) {
    // tslint:disable-next-line
    let data = this.ProductMulti.find(
      // tslint:disable-next-line
      (data: any) => data.product_type === productType,
    );
    return data ? data.product_code : '';
  }

  refundInfoByProduct(product) {
    if (product.product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE) {
      let refund = this.abreCuentasService.getDisbursementInfo();
      console.log('Refund: ', refund);
      refund = refund.find(
        data => data.product_type === product.product_type,
      ).refund;
      refund[0].account.account_subtype = parseFloat(
        this.abreCuentasService.getinfoInferredIncomes().account_type,
      );
      refund[0].account.account_type = 'AHO';
      return refund;
    } else if (product.product_type === BUSINESS_CONST.PRODUCT_TYPE_REVOLVING) {
      this.abreCuentasService.setStatusCreate(true);
      if (this.abreCuentasService.haveLoan) {
        let refund = this.loanRefund(product)[0];
        refund.account.account_type = 'AHO';
        refund.account.account_subtype = parseFloat(
          this.abreCuentasService.getinfoInferredIncomes().account_type,
        );
        refund.refund_method = 'AC';
        return [refund];
      } else {
        return [
          {
            refund_method: 'AC',
            account: {
              account_id: '',
              account_type: 'AHO',
              account_subtype: parseFloat(
                this.abreCuentasService.getinfoInferredIncomes().account_type,
              ),

            },
            branch_id: this.abreCuentasService.getAgencySelected()
              ? this.abreCuentasService.getAgencySelected().agency_code
              : '',
            amount: this.validAmountFirmlyApproved(product),
          },
        ];
      }
    } else if (product.product_type === BUSINESS_CONST.PRODUCT_TYPE_LOAN) {
      return this.loanRefund(product);
    } else {
      return [];
    }
  }

  loanRefund(product) {
    let refund = this.abreCuentasService.getDisbursementInfo();
    refund = refund.find(
      data => data.product_type === product.product_type,
    ).refund;
    return refund;
  }
  /**
   * Función que permite formatear las TASAS según sea el caso,
   * si es un Aprobado en Firme o no.
   * @param product
   * @param rate
   * @returns
   */
  private validRateFirmlyApproved(product, rate?) {
    let rate_prod = [{}];
    const exp_reg = /[.,%]/g;
    if (
      !this.firmlyApprovedService.isEmpty() &&
      this.firmlyApprovedService.getOfferType_Channel() === 'En_firme' &&
      product.product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE
    ) {
      const rate_firmlyApproved = this.abreCuentasService.getAbreCuentasModel().creditParms.rateValue;
      rate_prod = [
        {
          decimals: this.numberHelper.countDecimalDigits(
            rate_firmlyApproved.rate_NA.replace('%', '').replace(',', '.'),
          ),
          value: parseFloat(rate_firmlyApproved.rate_NA.replace(exp_reg, '')),
        },
      ];
    } else if (product.product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE ||
      this.abreCuentasService.haveLoan) {
      rate_prod = this.productRate(product, rate);
    } else if (product.product_type === BUSINESS_CONST.PRODUCT_TYPE_LIVING) {
      rate_prod = [this.abreCuentasService.getRatesOffer()];
    } else {
      rate_prod = [
        {
          value: 95,
          decimals: 1,
        },
      ];
    }
    return rate_prod;
  }

  productRate(product, rate) {
    const exp_reg = /[. ()]/g;
    let rateLoan;
    if (this.abreCuentasService.haveLoan) {
      let tasa = this.abreCuentasService.getRatesLoan();
      tasa = tasa.replace(exp_reg, '').split('%')[1];
      rateLoan = [{
        value: Number(tasa),
        decimals: 3,
      }];
    }
    rate = product.product_type === BUSINESS_CONST.PRODUCT_TYPE_FREE ? rate : rateLoan;
    return rate;
  }

  /**
   * Función que permite formatear las 3 tasas del Crédito Personal del
   * Aprobado en Firme
   * @param rates
   */
  private formatRateFirmlyApproved(rates) {
    const exp_reg = /[.,%]/g;
    return [
      {
        decimals: this.numberHelper.countDecimalDigits(
          rates.rate_EA.replace('%', '').replace(',', '.'),
        ),
        type: 'EA',
        value: parseFloat(rates.rate_EA.replace(exp_reg, '')),
      },
      {
        decimals: this.numberHelper.countDecimalDigits(
          rates.rate_MV.replace('%', '').replace(',', '.'),
        ),
        type: 'MV',
        value: parseFloat(rates.rate_MV.replace(exp_reg, '')),
      },
      {
        decimals: this.numberHelper.countDecimalDigits(
          rates.rate_NA.replace('%', '').replace(',', '.'),
        ),
        type: 'NA',
        value: parseFloat(rates.rate_NA.replace(exp_reg, '')),
      },
    ];
  }

  /**
   * Función que permite validar si el producto es un Aprobado en Firme
   * para formatear el campo amount.
   * @param product
   * @returns
   */
  private validAmountFirmlyApproved(product) {
    let amount_data = {};
    console.log(product);
    amount_data = {
      currency:
        product.limit_amount === null ? '' : product.limit_amount.currency,
      amount:
        product.limit_amount === null
          ? ''
          : parseInt(this.validAmount(product.limit_amount.amount), 10),
      decimals: 0,
    };
    return amount_data;
  }

  validAmount(amount: any): string {
    console.log('amount', amount);
    amount = amount.toString();
    amount = amount.includes('.') ? amount.replace(/[^0-9|-]/g, '') : amount;
    return amount.toString();
  }
  async getChannelNotification() {
    this.solicitationIdV = this.abreCuentasService.getSolicitationId();
    await this.requestService
      .getChannelNotification(this.solicitationIdV)
      .then((res: any) => {
        const dataPCO = JSON.parse(this.cipherService.decrypt(res.data.message));
        this.abreCuentasService.setChannelNotificationData(dataPCO);
      });
  }
}
